/**
 * HI/HA — 독립 스킬 화면.
 *
 * 액티브와 패시브를 도움말에서 떼어 실제 성장 화면으로 묶는다. 효과 수치는 서버가
 * 상태 패킷으로 준 현재/다음 값을 우선한다. 배포 중 옛 패킷에는 클라이언트 정본 미러를
 * 폴백으로 써 화면이 비지 않게 한다.
 *
 * 14.604(2026-10-21 사용자 승인) — 일반 스킬(직업 스킬·레벨 패시브)은 「+100 최대」, 서적·각인은 「Lv10 최대」가 끝이다(끝값은 서버
 * `you.rankEnds` — 콘솔 「기본 성장」). 단계 칸(점 10개)은 끝까지의 진척이고, 끝에서는 강화·읽기·새기기 버튼이 잠긴다.
 */
import { esc } from '../../core/state.js';
import {
  PASSIVES, passiveBookSourceText, passiveRankOf,
  SKILL_IMPRINTS, IMPRINT_PITY_KILLS, skillImprintRankOf,
} from '../../world/map.js';
import { lore, muted, num } from './common.js';

function passiveRows(y) {
  if (Array.isArray(y.passives) && y.passives.length) return y.passives;
  const learned = y.inv?.passives || [];
  const junk = y.inv?.junk || {};
  return PASSIVES.map((def) => {
    const rank = passiveRankOf(learned, def.id);
    // 옛 패킷 폴백 — 미러 문장은 Lv1~10 까지다(그 위는 서버 문장만 — 없으면 「확인 중」)
    return {
      id: def.id, key: def.key, name: def.name, from: passiveBookSourceText(def),
      rank,
      rare: !!def.rare,
      current: rank > 0 ? def.effects[rank - 1] ?? null : null,
      next: def.effects[rank] ?? null,
      held: Math.max(0, Math.floor(Number(junk[def.key]) || 0)),
    };
  });
}

function imprintRows(y) {
  if (Array.isArray(y.imprints) && y.imprints.length) return y.imprints;
  const learned = y.inv?.imprints || [];
  const junk = y.inv?.junk || {};
  return SKILL_IMPRINTS.map((def) => {
    const rank = skillImprintRankOf(learned, def.id);
    return {
      ...def, rank, equipped: y.inv?.imprint === def.id && rank > 0,
      current: rank ? def.effects[rank - 1] ?? null : null,
      next: def.effects[rank] ?? null,
      held: Math.max(0, Math.floor(Number(junk[def.key]) || 0)), pity: 0, pityMax: IMPRINT_PITY_KILLS,
    };
  });
}

/** 14.604 — 단계의 끝(서버 `you.rankEnds` · 옛 패킷은 기본값 +100 · Lv10) */
function rankEnds(y) {
  const skill = Math.floor(Number(y?.rankEnds?.skill)), book = Math.floor(Number(y?.rankEnds?.book));
  return { skill: skill >= 10 ? skill : 100, book: book >= 1 ? book : 10 };
}
/** 단계 칸 — 점 10개가 끝(`end`)까지의 진척이다(서적·각인은 한 점이 한 단계, 일반 스킬은 끝/10 단계). 끝이면 「최대」 */
function pips(rank, end) {
  const lit = rank >= end ? 10 : Math.floor((rank / end) * 10);
  return `<span class="skillpips" aria-label="${num(rank)}단계 / 최대 ${num(end)}">`
    + Array.from({ length: 10 }, (_, i) => `<i${i < lit ? ' class="on"' : ''}></i>`).join('')
    + (rank >= end ? '<b class="skillpipsover">최대</b>' : '') + '</span>';
}

export function render({ y }) {
  const skills = y.skills || [];
  const active = skills.filter(s => !s.passive);
  const free = Math.max(0, Math.floor(Number(y.skillFree) || 0));
  const passives = passiveRows(y);
  const imprints = imprintRows(y);
  const learned = passives.filter((p) => p.rank > 0).length;
  const books = passives.reduce((n, p) => n + p.held, 0);
  const stones = imprints.reduce((n, p) => n + p.held, 0);
  const ends = rankEnds(y);

  let h = `<div class="skilldash ux-facts">
    <div class="ux-fact ux-primary"><small>스킬 포인트</small><b class="ux-number">${num(free)}</b></div>
    <div class="ux-fact"><small>액티브</small><b class="ux-number">${active.length}/${y.skillTotal || active.length}</b></div>
    <div class="ux-fact"><small>패시브</small><b class="ux-number">${learned}/${passives.length}</b></div>
    <div class="ux-fact"><small>성장 재료</small><b class="ux-number">${num(books + stones)}</b></div>
  </div><nav class="dexnav" aria-label="스킬 분류"><a href="#skill-class">직업 스킬</a><a href="#skill-imprints">각인</a>${y.levelPassives?.length ? '<a href="#skill-level">레벨 패시브</a>' : ''}<a href="#skill-books">드롭 패시브</a></nav>`;


  /**
   * 묶음 UI2b(DESIGN 14.439) — 첫 화면은 조작할 것(스킬 카드)부터. 규칙 문단은 각 절 **카드 아래 접힌 「규칙」** 한 줄로 — 전에는 7줄 설명 뒤에
   * 첫 카드가 폰 화면 아래 끝에 걸렸다(2차 평론 N8).
   */
  h += `<div id="skill-class" class="sec"><h3>직업 스킬</h3>
    <div class="skillcards">`;
  h += skills.length ? skills.map((s) => {
    const next = s.nextBoost ?? null;
    const atEnd = s.rank >= ends.skill;
    const disabled = free < 1 || atEnd;
    return `<article class="skillcard">
      <div class="skilltitle"><span>${s.passive ? '<em>상시</em>' : `<small>${active.indexOf(s) + 1}</small>`}${esc(s.name)}</span>
        <b>+${num(s.rank)} <small>/ +${num(ends.skill)} 최대</small></b></div>
      <div class="skillstate"><span>${s.passive ? '항상 적용' : s.cd > 0 ? `${Math.ceil(s.cd)}초 뒤` : '사용 가능'}</span>
        ${s.passive ? '' : `<span>${s.auto ? '자동 사용 켜짐' : '자동 사용 꺼짐'}</span>`}${pips(s.rank, ends.skill)}</div>
      <p>${esc(s.desc)}</p>
      <div class="skillpath"><span>현재 <b>${s.passive ? esc(s.currentEffect || '효과 확인 중') : `+${s.boost}%`}</b></span><i>→</i>
        <span>다음 <b>${atEnd ? '최대 단계' : s.passive ? esc(s.nextEffect || '효과 확인 중') : next == null ? '확인 중' : `+${next}%`}</b></span></div>
      <button class="wide" onclick="act.skillUp('${s.id}')"${disabled ? ' disabled' : ''}>${atEnd ? `+${num(ends.skill)} 최대` : free < 1 ? '스킬 포인트 없음' : '+1 강화 · 1점'}</button>
      ${s.passive ? '' : `<button class="wide${s.auto ? ' gold' : ''}" onclick="act.autoSkill('${s.id}',${s.auto ? 'false' : 'true'})">${s.auto ? '자동 사용 끄기' : '자동 사용 켜기'}</button>`}
    </article>`;
  }).join('') : muted('레벨 5에 첫 스킬을 자동으로 습득한다.');
  h += '</div>' + lore('규칙', `레벨이 되면 자동으로 익힌다. 5레벨마다 포인트 1점, 1점마다 +1 — <b>+${num(ends.skill)}이 최대</b>다(+10까지 단계마다 본래 효과 +8%, 그 위는 +${num(ends.skill)}에서 +300%에 닿게 고르게). 단축키는 1~6이다.<br>`
    + '직업 패시브는 항상 적용되며 따로 사용하지 않는다. 현재·다음 효과를 카드에서 비교할 수 있다. 기존 연막·올무화살의 강화 단계는 그대로 이어진다. 매의 눈은 몬스터·플레이어와의 일반 전투 사거리를 늘리며, 경비병 교전·허수아비 훈련 거리는 유지된다.')
    + '</div>';
  /**
   * EXCHANGE-R1 — 남는 스킬 포인트의 처분처. 없으면 버튼도 없다(회색 버튼은 질문만 만든다).
   * 묶음 AB — 되돌릴 수 없는 교환은 스킬 카드 **아래**, 한 겹 접힌 자리에 둔다. 전에는 창 맨 위 가장 큰 버튼이라 [+1 강화] 보다 먼저 눌렸다.
   */
  if (free >= 1) {
    h += `<details class="skill-exchange"><summary>남는 스킬 포인트를 보너스 스탯으로 바꾸기</summary>`
      + muted('스킬 포인트 1점을 보너스 스탯 12로 바꾼다. 되돌릴 수 없다.')
      + `<button class="wide" onclick="act.skillToStat()">스킬 포인트 1 → 보너스 스탯 12 (되돌릴 수 없음)</button></details>`;
  }

  const equipped = imprints.find((p) => p.equipped);
  h += `<div id="skill-imprints" class="sec"><h3>스킬 각인 <em>${equipped ? `${esc(equipped.name)} Lv${equipped.rank}` : '미장착'}</em></h3>
    <div class="skillcards passivecards">`;
  h += imprints.map((p) => {
    const atEnd = p.rank >= ends.book;
    const canRead = p.held > 0 && !atEnd;
    return `<article class="skillcard passivecard${p.equipped ? ' rare' : ''}">
      <div class="skilltitle"><span>${p.equipped ? '<em>장착 중</em>' : ''}${esc(p.name)}</span><b>Lv${num(p.rank)} <small>/ Lv${num(ends.book)} 최대</small></b></div>
      <div class="skillstate"><span>${p.held ? `각인석 ${num(p.held)}개` : `확정 진행 ${p.pity || 0}/${p.pityMax || IMPRINT_PITY_KILLS}`}</span>${pips(p.rank, ends.book)}</div>
      <p class="qt">${esc(p.from || '')}</p>
      <div class="passiveeffect"><small>현재</small><p>${p.current ? esc(p.current) : '미습득'}</p></div>
      ${atEnd ? '' : `<div class="passiveeffect next"><small>다음 Lv${num(p.rank + 1)}</small>
        <p>${esc(p.next || '효과 확인 중')}</p></div>`}
      ${p.rank > 0
        ? `<button class="wide${p.equipped ? ' gold' : ''}" onclick="act.imprintEquip('${p.equipped ? '' : p.id}')">${p.equipped ? '장착 해제' : '전투 각인 장착'}</button>`
        : ''}
      <button class="wide" onclick="act.imprintRead('${p.id}')"${canRead ? '' : ' disabled'}>${atEnd ? `Lv${num(ends.book)} 최대` : p.held ? `Lv${num(p.rank + 1)}로 새긴다 · 1개` : '각인석이 필요하다'}</button>
    </article>`;
  }).join('');
  h += '</div>' + lore('규칙', `유리심연 2~4층의 상징 보스가 12% 확률로 완성석을 주며, 같은 보스 ${IMPRINT_PITY_KILLS}회째에는 반드시 나온다. 같은 각인석을 새길 때마다 한 단계씩 강해지며 <b>Lv${num(ends.book)}이 최대</b>다(최대에서는 새기지 않고 각인석이 남는다). 한 번에 하나만 장착할 수 있다.`) + '</div>';

  // LEVEL-PASSIVE-R2 — 레벨로 자동 얻고, 스킬 포인트로 단계 강화(+8%/단계). 죽어도 잃지 않는다.
  const levelPassives = y.levelPassives || [];
  if (levelPassives.length) {
    h += `<div id="skill-level" class="sec"><h3>레벨 패시브 <em>${levelPassives.length}종</em></h3>
      <div class="skillcards passivecards">`
      + levelPassives.map((lp) => {
        const rank = Number(lp.rank) || 0;
        const atEnd = rank >= ends.skill;
        const canUp = free >= 1 && !atEnd;
        return `<article class="skillcard passivecard">
        <div class="skilltitle"><span>${esc(lp.name)}</span><b>+${num(rank)} <small>/ +${num(ends.skill)} 최대</small></b></div>
        <div class="skillstate">${pips(rank, ends.skill)}</div>
        <div class="passiveeffect"><small>현재</small><p>${esc(lp.current || lp.desc)}</p></div>
        ${atEnd ? '' : `<div class="passiveeffect next"><small>다음 +${num(rank + 1)}</small>
          <p>${esc(lp.next || '효과 확인 중')}</p></div>`}
        <button class="wide" onclick="act.levelPassiveUp('${lp.id}')"${canUp ? '' : ' disabled'}>${atEnd ? `+${num(ends.skill)} 최대` : free >= 1 ? '+1 강화 · 1점' : '스킬 포인트 없음'}</button>
      </article>`;
      }).join('')
      + '</div>' + lore('규칙', `레벨에 도달하면 자동으로 익힌다. 스킬 포인트 1점마다 +1 — <b>+${num(ends.skill)}이 최대</b>다(+10까지 단계마다 본래 효과 +8%, 그 위는 +${num(ends.skill)}에서 +300%에 닿게 고르게 · 공격 쿨 감소는 70%, 이동·회피는 +50%가 끝). 레벨이 오를수록 효과도 커진다. 죽어도 잃지 않는다.`) + '</div>';
  }

  h += `<div id="skill-books" class="sec"><h3>드롭 패시브</h3>
    <div class="skillcards passivecards">`;
  h += passives.map((p) => {
    const atEnd = p.rank >= ends.book;
    const disabled = p.held < 1 || atEnd;
    return `<article class="skillcard passivecard${p.rare ? ' rare' : ''}">
      <div class="skilltitle"><span>${p.rare ? '<em>최희귀</em>' : ''}${esc(p.name)}</span><b>Lv${num(p.rank)} <small>/ Lv${num(ends.book)} 최대</small></b></div>
      <div class="skillstate"><span>${p.held ? `보유 ${num(p.held)}권` : '보유 책 없음'}</span>${pips(p.rank, ends.book)}</div>
      <p class="qt">${esc(p.from || '')}</p>
      <div class="passiveeffect"><small>현재</small><p>${p.current ? esc(p.current) : '미습득'}</p></div>
      ${atEnd ? '' : `<div class="passiveeffect next"><small>다음 Lv${num(p.rank + 1)}</small>
        <p>${esc(p.next || '효과 확인 중')}</p></div>`}
      <button class="wide" onclick="act.read('${p.id}')"${disabled ? ' disabled' : ''}>${atEnd ? `Lv${num(ends.book)} 최대` : p.held ? `Lv${num(p.rank + 1)}로 읽는다 · 1권` : '책이 필요하다'}</button>
    </article>`;
  }).join('');
  h += '</div>' + lore('규칙', `뿔왕은 모든 패시브 서적을, 본토 갱도 1층~유리심연 1층의 상징 보스는 자기 한 권만 드물게 떨군다. 같은 책을 읽을 때마다 한 단계씩 오르며 <b>Lv${num(ends.book)}이 최대</b>다(최대에서는 읽지 않고 책이 남는다). 습득한 단계는 사망해도 유지된다. 자홍색 「최희귀」 서적은 상시 효과가 강해 일반 서적보다 4배 이상 드물다. 조건형 피해 보너스는 동시에 터지면 합이 +60%를 넘는 몫부터 완만하게 오른다.`) + '</div>';
  return h;
}
